import html2canvas from 'html2canvas';
import { jsPDF as JsPdf } from 'jspdf';
import * as XLSX from 'xlsx';

import { ReportingBucket } from '../types/buckets';
import type { IFactTable } from '../types/metrics';
import type { IResolvedPeriod } from '../types/period';
import type { ITableModel } from '../types/tables';
import { BUCKET_LABELS } from './BucketHelper';

export interface IPdfExportInput {
  companyName: string;
  periodLabel: string;
  comparisonLabel: string;
  booksClosedThrough: string;
  /** Canvas root; every `[data-report-item]` inside is rasterised at its on-screen position. */
  root: HTMLElement;
}

export interface IXlsxExportInput {
  companyName: string;
  currency: string;
  resolved: IResolvedPeriod;
  booksClosedThrough: string;
  table: IFactTable;
  tables: { title: string; model: ITableModel }[];
  /** Term definitions from column headers, written as numbered footnotes on the Summary sheet. */
  definitions?: { term: string; definition: string }[];
}

interface IPlacedItem {
  element: HTMLElement;
  x: number;
  y: number;
  w: number;
  h: number;
}

type SheetCell = string | number | null;

const PAGE_W = 297;
const PAGE_H = 210;
const MARGIN = 10;
const HEADER_H = 14;
const FOOTER_H = 8;

/** PDF (snapshot of the live canvas) and XLSX (table models + bucket/ledger data) exports. */
export class ReportExportHelper {
  static readonly ITEM_SELECTOR = '[data-report-item]';

  static readonly IGNORE_ATTRIBUTE = 'data-export-ignore';

  /** Renders the canvas to landscape A4 pages, preserving each component's grid position. */
  static async downloadPdf(input: IPdfExportInput): Promise<void> {
    const elements = Array.from(
      input.root.querySelectorAll<HTMLElement>(ReportExportHelper.ITEM_SELECTOR)
    );
    if (elements.length === 0) return;

    const rootRect = input.root.getBoundingClientRect();
    const scale = (PAGE_W - MARGIN * 2) / rootRect.width;
    const contentTop = MARGIN + HEADER_H;
    const usableH = PAGE_H - contentTop - MARGIN - FOOTER_H;
    const pdf = new JsPdf({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4',
    });

    const placed = elements
      .map<IPlacedItem>((element) => {
        const r = element.getBoundingClientRect();
        return {
          element,
          x: (r.left - rootRect.left) * scale,
          y: (r.top - rootRect.top) * scale,
          w: r.width * scale,
          h: r.height * scale,
        };
      })
      .sort((a, b) => a.y - b.y || a.x - b.x);

    const images = await Promise.all(
      placed.map((item) =>
        html2canvas(item.element, {
          scale: 2,
          backgroundColor: '#ffffff',
          logging: false,
          useCORS: true,
          ignoreElements: (el) =>
            el.hasAttribute(ReportExportHelper.IGNORE_ATTRIBUTE),
        })
      )
    );

    ReportExportHelper.drawPageChrome(pdf, input, 1);
    placed.reduce(
      (page, item, index) => {
        const next =
          item.y + item.h - page.offset > usableH
            ? { number: page.number + 1, offset: item.y }
            : page;
        if (next !== page) {
          pdf.addPage();
          ReportExportHelper.drawPageChrome(pdf, input, next.number);
        }
        const image = images[index];
        if (image) {
          pdf.addImage(
            image.toDataURL('image/png'),
            'PNG',
            MARGIN + item.x,
            contentTop + (item.y - next.offset),
            item.w,
            item.h,
            undefined,
            'FAST'
          );
        }
        return next;
      },
      { number: 1, offset: 0 }
    );

    pdf.save(
      ReportExportHelper.fileName(input.companyName, input.periodLabel, 'pdf')
    );
  }

  static downloadXlsx(input: IXlsxExportInput): void {
    const workbook = ReportExportHelper.buildWorkbook(input);
    XLSX.writeFile(
      workbook,
      ReportExportHelper.fileName(
        input.companyName,
        input.resolved.label,
        'xlsx'
      )
    );
  }

  static buildWorkbook(input: IXlsxExportInput): XLSX.WorkBook {
    const workbook = XLSX.utils.book_new();
    const usedNames = new Set<string>();
    const generatedAt = new Date().toISOString();
    const footer = `${input.companyName} · ${input.resolved.label} ${input.resolved.comparisonLabel} · books closed ${input.booksClosedThrough} · generated ${generatedAt}`;
    const append = (title: string, rows: SheetCell[][]) =>
      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.aoa_to_sheet(rows),
        ReportExportHelper.sheetName(title, usedNames)
      );

    append('Summary', [
      ['Company', input.companyName],
      ['Reporting currency', input.currency],
      ['Period', input.resolved.label],
      ['Comparison basis', input.resolved.comparisonLabel],
      ['Books closed through', input.booksClosedThrough],
      ['Generated at', generatedAt],
      [],
      [
        'Percent columns are in percentage points. Flows are summed over the period; balances are closing balances.',
      ],
      ...ReportExportHelper.footnoteRows(input.definitions ?? []),
    ]);

    input.tables.forEach((t) =>
      append(t.title, [
        ...ReportExportHelper.tableToRows(t.model),
        [],
        [footer],
      ])
    );

    const { months } = input.table;
    append('Buckets by month', [
      ['Bucket', ...months],
      ...Object.values(ReportingBucket).map<SheetCell[]>((b) => [
        BUCKET_LABELS[b],
        ...months.map((m) => input.table.byMonth.get(m)?.buckets[b] ?? 0),
      ]),
    ]);

    append('Ledger lines', [
      ['Ledger line', 'Section', 'Bucket', ...months],
      ...input.table.lines.map<SheetCell[]>((line) => [
        line.name,
        line.section,
        BUCKET_LABELS[
          input.table.bucketMap.get(line.id) ?? ReportingBucket.EXCLUDED
        ],
        ...months.map((m) => input.table.byMonth.get(m)?.lines[line.id] ?? 0),
      ]),
    ]);

    return workbook;
  }

  private static drawPageChrome(
    pdf: JsPdf,
    input: IPdfExportInput,
    pageNo: number
  ): void {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(13);
    pdf.text(input.companyName, MARGIN, MARGIN + 5);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.text(
      `${input.periodLabel} · ${input.comparisonLabel}`,
      MARGIN,
      MARGIN + 10
    );
    pdf.setFontSize(7.5);
    pdf.setTextColor(110);
    pdf.text(
      `Books closed through ${input.booksClosedThrough} · Generated ${new Date().toLocaleString()} · Page ${pageNo}`,
      MARGIN,
      PAGE_H - MARGIN + 2
    );
    pdf.setTextColor(0);
  }

  private static footnoteRows(
    definitions: { term: string; definition: string }[]
  ): SheetCell[][] {
    if (definitions.length === 0) return [];
    return [
      [],
      ['Definitions'],
      ...definitions.map<SheetCell[]>((d, i) => [
        `${i + 1}. ${d.term}`,
        d.definition,
      ]),
    ];
  }

  private static tableToRows(model: ITableModel): SheetCell[][] {
    const header = model.columns.map((c) => c.label || 'Line');
    const body = model.rows.map<SheetCell[]>((row) => [
      `${'  '.repeat(row.indent ?? 0)}${row.label}`,
      ...row.cells.map((cell, i) => {
        const format = model.columns[i + 1]?.format;
        const isPercent = format === 'percent' || format === 'signedPercent';
        return typeof cell === 'number' && isPercent
          ? Math.round(cell * 10000) / 100
          : cell;
      }),
    ]);
    return [header, ...body];
  }

  /** Excel sheet names are capped at 31 chars and must be unique within a workbook. */
  private static sheetName(title: string, used: Set<string>): string {
    const base = title.replace(/[\\/?*[\]:]/g, '').slice(0, 28);
    const candidates = [
      base,
      ...Array.from({ length: 99 }, (_, i) => `${base} ${i + 2}`),
    ];
    const name =
      candidates.find((c) => !used.has(c)) ?? `${base} ${Date.now()}`;
    used.add(name);
    return name;
  }

  private static fileName(
    companyName: string,
    periodLabel: string,
    extension: string
  ): string {
    const slug = (value: string) =>
      value.replace(/[^a-z0-9]+/gi, '-').toLowerCase();
    return `${slug(companyName)}-management-report-${slug(periodLabel)}.${extension}`;
  }
}
